import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    ChevronLeft, Loader2, Info, RefreshCcw, 
    ArrowRight, User, Building2, Layers, EyeOff, Eye
} from 'lucide-react';
import { useToast } from '../hooks/useToast';
import { fetchLedgerSummaryList, currencyCodeToSymbolMap } from '../apiHelper';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const formatCurrency = (amount, currencyCode = 'NGN') => {
    const symbol = currencyCodeToSymbolMap[currencyCode] || '₦';
    return `${symbol}${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount)}`;
};

const LedgerSummaryReport = () => {
    const { showToast } = useToast();
    const [summaryData, setSummaryData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hideZeroBalances, setHideZeroBalances] = useState(true); // Default to hiding zeros
    const navigate = useNavigate();

    const fetchSummary = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchLedgerSummaryList();
            setSummaryData(data);
        } catch (err) {
            showToast("Failed to load ledger summary.", "error");
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => { fetchSummary(); }, [fetchSummary]);

    // Memoized filtered data to prevent unnecessary recalculations
    const filteredData = useMemo(() => {
        if (!hideZeroBalances) return summaryData;
        return summaryData.filter(item => Math.abs(item.balance) > 0.001);
    }, [summaryData, hideZeroBalances]);

    const handleAccountClick = (item) => {
        const baseUrl = `/ledger-detail/${item.account_id}`;
        const query = item.is_subsidiary ? `?customer_id=${item.customer_id}` : '';
        // navigate(`${baseUrl}${query}`);
        // console.log(baseUrl)
        // console.log(query)
        // console.log(item)
        if (!query) {
            navigate('/reports/account-ledger-report', { state: { account_id: item.account_id } });
        }
    };

    return (
        <div className="p-0 sm:p-4 md:p-6 lg:p-8 bg-[#f8f9fa] min-h-screen font-sans text-slate-900">
            <div className="max-w-5xl mx-auto">
                {/* Header Section */}
                <header className="mb-6 px-4 sm:px-0">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <Button 
                                onClick={() => navigate(-1)} 
                                variant="ghost" 
                                className="p-0 h-auto mb-2 text-slate-500 hover:text-indigo-600 flex items-center"
                            >
                                <ChevronLeft size={16} /> 
                                <span className="text-xs uppercase tracking-wider font-bold">Back</span>
                            </Button>
                            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Ledger Summary</h1>
                            <p className="text-sm text-slate-500 font-medium">Consolidated view of all financial positions</p>
                        </div>

                        <div className="flex items-center gap-3">
                            {/* Toggle Checkbox for Zero Balances */}
                            <label className="flex items-center gap-2 bg-white border border-slate-200 px-3 py-2 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors shadow-sm">
                                <input 
                                    type="checkbox" 
                                    checked={hideZeroBalances}
                                    onChange={(e) => setHideZeroBalances(e.target.checked)}
                                    className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300"
                                />
                                <span className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
                                    {hideZeroBalances ? <EyeOff size={14}/> : <Eye size={14}/>}
                                    Hide Zero Balances
                                </span>
                            </label>

                            <Button 
                                onClick={fetchSummary} 
                                disabled={loading} 
                                className="bg-indigo-600 text-white hover:bg-indigo-700 rounded-xl px-4 py-2 flex items-center gap-2 shadow-md shadow-indigo-200"
                            >
                                {loading ? <Loader2 className="animate-spin" size={16} /> : <RefreshCcw size={16} />}
                                <span className="hidden sm:inline text-sm font-bold">Refresh</span>
                            </Button>
                        </div>
                    </div>

                    {/* Quick Stats Bar */}
                    {!loading && summaryData.length > 0 && (
                        <div className="mt-4 flex gap-4 overflow-x-auto pb-2">
                            <div className="bg-white px-4 py-2 rounded-lg border border-slate-100 shadow-sm flex items-center gap-2 shrink-0">
                                <span className="text-[10px] font-black uppercase text-slate-400">Total Ledgers</span>
                                <span className="text-sm font-bold text-indigo-600">{summaryData.length}</span>
                            </div>
                            <div className="bg-white px-4 py-2 rounded-lg border border-slate-100 shadow-sm flex items-center gap-2 shrink-0">
                                <span className="text-[10px] font-black uppercase text-slate-400">Hidden</span>
                                <span className="text-sm font-bold text-slate-500">{summaryData.length - filteredData.length}</span>
                            </div>
                        </div>
                    )}
                </header>

                <Card className="overflow-hidden border-none shadow-xl shadow-indigo-100/50 rounded-2xl bg-white">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-80 text-slate-400">
                            <Loader2 className="animate-spin mb-4 text-indigo-500" size={40} />
                            <p className="text-sm font-medium animate-pulse uppercase tracking-widest">Calculating Balances...</p>
                        </div>
                    ) : filteredData.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-80 text-slate-400 px-6 text-center">
                            <Info size={40} className="mb-3 opacity-20 text-indigo-900" />
                            <p className="text-sm font-medium italic">No ledgers match the current filter.</p>
                            {hideZeroBalances && summaryData.length > 0 && (
                                <button 
                                    onClick={() => setHideZeroBalances(false)}
                                    className="mt-2 text-indigo-600 text-xs font-bold underline"
                                >
                                    Show accounts with zero balance
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-hidden">
                            {/* Desktop Table View */}
                            <div className="hidden md:block">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50/80 border-b border-slate-100">
                                            <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Origin</th>
                                            <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Account Details</th>
                                            <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Code</th>
                                            <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Current Balance</th>
                                            <th className="px-6 py-4 w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {filteredData.map((item) => (
                                            <tr 
                                                key={item.id} 
                                                onClick={() => handleAccountClick(item)}
                                                className="group cursor-pointer hover:bg-indigo-50/40 transition-all"
                                            >
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold uppercase ${item.is_subsidiary ? 'text-amber-600 bg-amber-50' : 'text-indigo-600 bg-indigo-50'}`}>
                                                        {item.is_subsidiary ? <User size={12} /> : <Building2 size={12} />}
                                                        {item.is_subsidiary ? 'Sub' : 'GL'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="font-bold text-slate-800 group-hover:text-indigo-700 transition-colors">{item.name}</div>
                                                    <div className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
                                                        <Layers size={10} /> {item.path}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 font-mono text-xs text-slate-500">{item.code}</td>
                                                <td className={`px-6 py-4 text-right font-mono font-bold text-sm ${item.balance < 0 ? 'text-red-500' : 'text-slate-900'}`}>
                                                    {formatCurrency(item.balance)}
                                                </td>
                                                <td className="px-6 py-4 text-slate-300 group-hover:text-indigo-500">
                                                    <ArrowRight size={16} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile List View */}
                            <div className="md:hidden divide-y divide-slate-100">
                                {filteredData.map((item) => (
                                    <div 
                                        key={item.id} 
                                        onClick={() => handleAccountClick(item)}
                                        className="p-5 active:bg-slate-50 flex justify-between items-center gap-4"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded ${item.is_subsidiary ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}`}>
                                                    {item.is_subsidiary ? 'Subsidiary' : 'Main GL'}
                                                </span>
                                                <span className="text-[10px] font-mono text-slate-400">{item.code}</span>
                                            </div>
                                            <h3 className="font-bold text-slate-900 truncate mb-0.5">{item.name}</h3>
                                            <p className="text-[9px] text-slate-500 truncate uppercase tracking-tighter">{item.root_type}</p>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className={`font-mono font-bold text-sm ${item.balance < 0 ? 'text-red-500' : 'text-slate-900'}`}>
                                                {formatCurrency(item.balance)}
                                            </div>
                                            <ArrowRight size={14} className="ml-auto mt-2 text-slate-300" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default LedgerSummaryReport;